import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppContext } from '../../App';
import './register.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const { userCtx, setUserCtx } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit';
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    email: '',
    nationality: '',
    birthdate: '',
    address: '',
    gender: '',
    mobileNumber: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalData, setPasswordModalData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordModalError, setPasswordModalError] = useState('');
  const navigate = useNavigate();

  // Helper function to extract date from ISO string (e.g., "1971-07-17T00:00:00.000Z" -> "1971-07-17")
  const extractDateFromISO = (isoString) => {
    console.log('Extracting date from ISO string:', isoString);
    if (!isoString) return '';
    try {
      // Extract just the date part from ISO string
      const dateOnly = isoString.split('T')[0];
      return dateOnly;
    } catch (error) {
      console.error('Error parsing date:', error);
      return '';
    }
  };

  // Helper function to format date from yyyy-mm-dd to mm/dd/yyyy
  const formatDateToDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to format date from mm/dd/yyyy to yyyy-mm-dd for HTML input
  const formatDateToInput = (dateString) => {
    console.log('Formatting date for input:', dateString);
    if (!dateString) return '';
    // If it's already in yyyy-mm-dd format, return as is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return dateString;
    
    // Handle mm/dd/yyyy format
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  // Load user data in edit mode
  useEffect(() => {
    if (isEditMode && userCtx) {
      console.log('User context data:', userCtx); // Debug log to see available properties
      // Populate form with existing user data
      setFormData(prevData => ({
        ...prevData,
        name: userCtx.username || userCtx.name || '',
        email: userCtx.email || '',
        nationality: userCtx.nationality || '',
        birthdate: extractDateFromISO(userCtx.birthday),
        address: userCtx.address || '',
        gender: userCtx.gender || '',
        mobileNumber: userCtx.mobile || '',
        // In edit mode, show asterisks for password length
        password: userCtx.password ? '*'.repeat(8) : '****', // Show 8 asterisks or default
        confirmPassword: ''
      }));
    }
    console.log('birthdate:', formData.birthdate);
  }, [isEditMode, userCtx]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
    setSuccess('');
  };

  const handlePasswordModalChange = (e) => {
    const { name, value } = e.target;
    setPasswordModalData({ ...passwordModalData, [name]: value });
    setPasswordModalError('');
  };

  const handlePasswordModalSubmit = async () => {
    setPasswordModalError('');

    // Validate required fields
    if (!passwordModalData.currentPassword) {
      setPasswordModalError('Please enter your current password.');
      return;
    }

    if (!passwordModalData.newPassword) {
      setPasswordModalError('Please enter a new password.');
      return;
    }

    if (!passwordModalData.confirmNewPassword) {
      setPasswordModalError('Please confirm your new password.');
      return;
    }

    // Validate new password length
    if (passwordModalData.newPassword.length < 6) {
      setPasswordModalError('New password must be at least 6 characters long.');
      return;
    }

    // Validate passwords match
    if (passwordModalData.newPassword !== passwordModalData.confirmNewPassword) {
      setPasswordModalError('New passwords do not match.');
      return;
    }

    try {
      // Call backend to update password
      const response = await fetch(`${BACKEND_URL}/update-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          oldPassword: passwordModalData.currentPassword,
          newPassword: passwordModalData.newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setPasswordModalError(errorData.error || 'Failed to update password. Please try again.');
        return;
      }

      // Success - update the form data with asterisks matching new password length
      setFormData(prev => ({
        ...prev,
        password: '*'.repeat(passwordModalData.newPassword.length),
        confirmPassword: ''
      }));

      // Reset modal and close
      setPasswordModalData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setShowPasswordModal(false);
      setSuccess('Password updated successfully!');
    } catch (error) {
      setPasswordModalError('Failed to update password. Please try again.');
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordModalData({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setPasswordModalError('');
  };

  // Check uniqueness of name and email
  const checkUnique = async () => {
    try {
      const payload = { 
        name: formData.name, 
        email: formData.email 
      };
      
      // In edit mode, exclude current user from uniqueness check
      if (isEditMode && userCtx) {
        payload.excludeUserId = userCtx._id || userCtx.id;
      }
      
      const res = await fetch(`${BACKEND_URL}/check-unique`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      return data; // { nameExists: bool, emailExists: bool }
    } catch (err) {
      return { nameExists: false, emailExists: false };
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      navigate('/userTrips'); // Go back to user trips in edit mode
    } else {
      navigate('/login'); // Go to login in register mode
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // In edit mode, password is optional and handled separately via modal
    if (!isEditMode) {
      // Registration mode - validate password match
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        setLoading(false);
        return;
      }
    }

    // Check uniqueness only if not in edit mode or if email changed (name can't change in edit mode)
    if (!isEditMode || (isEditMode && formData.email !== userCtx?.email)) {
      const unique = await checkUnique();
      if (unique.nameExists && !isEditMode) { // Only check name in registration mode
        setError('Username already exists.');
        setLoading(false);
        return;
      }
      if (unique.emailExists) {
        setError('Email already exists.');
        setLoading(false);
        return;
      }
    }

    // Prepare data for submission
    const submissionData = { ...formData };
    
    // In edit mode, handle password specially
    if (isEditMode) {
      // Only include password if it was updated via modal (not asterisks)
      if (formData.password.startsWith('*')) {
        // Password wasn't changed, remove from submission
        delete submissionData.password;
        delete submissionData.confirmPassword;
      }
      // Remove name from submission in edit mode since it's disabled
      delete submissionData.name;
    }

    // Submit registration or update
    const endpoint = isEditMode ? '/update-profile' : '/register';
    const requestOptions = {
      method: isEditMode ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(submissionData),
      credentials: 'include',
    };

    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, requestOptions);
      if (!res.ok) throw new Error(`${isEditMode ? 'Update' : 'Registration'} failed`);
      
      const successMessage = isEditMode 
        ? 'Profile updated successfully!' 
        : 'Registration successful! Redirecting to login...';
      setSuccess(successMessage);
      
      // If it's a successful profile update, update the user context
      if (isEditMode) {
        const updatedUserData = {
          ...userCtx,
          ...submissionData,
          // Keep the original properties that weren't in the form
          _id: userCtx._id,
          id: userCtx.id,
          username: userCtx.username || userCtx.name,
          name: userCtx.name || userCtx.username,
          // Update the birthday field to maintain ISO format if it was changed
          birthday: submissionData.birthdate ? `${submissionData.birthdate}T00:00:00.000Z` : userCtx.birthday,
          // Keep mobile in the format the backend expects
          mobile: submissionData.mobileNumber || userCtx.mobile
        };
        setUserCtx(updatedUserData);
        console.log('Updated user context:', updatedUserData);
      }
      
      setTimeout(() => {
        if (isEditMode) {
          navigate('/userTrips');
        } else {
          navigate('/login');
        }
      }, 1500);
    } catch (err) {
      setError(`${isEditMode ? 'Profile update' : 'Registration'} failed. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container stylish-register">
      {/* Beta notice only in registration mode */}
      {!isEditMode && (
        <div className="beta-note" style={{
          background: '#fffbe6',
          color: '#8a6d3b',
          border: '1px solid #faebcc',
          borderRadius: 6,
          padding: '10px 16px',
          marginBottom: 18,
          fontSize: '1.05em',
          textAlign: 'center',
          fontWeight: 500
        }}>
          <span>
            <strong>Note:</strong> The application is currently in controlled beta. Registered users must be activated before they can log in. This may take a few days.
          </span>
        </div>
      )}
      {/* Cancel button only for desktop */}
      <button className="cancel-btn desktop-cancel" onClick={handleCancel} title="Cancel">&times;</button>
      <h3>{isEditMode ? 'Update Profile' : 'User Registration'}</h3>
      <form onSubmit={handleSubmit} className="register-form">
        <div className="row">
          <div>
            <label htmlFor="name">User Name</label>
            <input 
              type="text" 
              id="name" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              required 
              disabled={isEditMode}
              style={isEditMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
          </div>
          <div>
            <label htmlFor="email">Email</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>
        </div>
        <div className="row">
          {!isEditMode ? (
            // Registration mode - show normal password fields
            <>
              <div>
                <label htmlFor="password">Password</label>
                <input 
                  type="password" 
                  id="password" 
                  name="password" 
                  value={formData.password} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div>
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
            </>
          ) : (
            // Edit mode - show current password (asterisks) and update button
            <>
              <div>
                <label htmlFor="password">Current Password</label>
                <input 
                  type="password" 
                  id="password" 
                  name="password" 
                  value={formData.password} 
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
              <div>
                <div style={{ height: '1.8rem', marginBottom: '0.3rem' }}></div>
                <button 
                  type="button" 
                  id="updatePasswordBtn"
                  className="update-password-btn"
                  onClick={() => setShowPasswordModal(true)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.7rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    background: '#007bff',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'border 0.2s',
                    boxSizing: 'border-box'
                  }}
                >
                  Update Password
                </button>
              </div>
            </>
          )}
        </div>
        <div className="row">
          <div>
            <label htmlFor="nationality">Nationality</label>
            <input type="text" id="nationality" name="nationality" value={formData.nationality} onChange={handleChange} />
          </div>
          <div>
            <label htmlFor="birthdate">Birthdate </label>
            <input 
              type="date" 
              id="birthdate" 
              name="birthdate" 
              value={formData.birthdate} 
              onChange={handleChange}
              placeholder="MM/DD/YYYY"
            />
            
          </div>
        </div>
        <div className="row">
          <div>
            <label htmlFor="gender">Gender</label>
            <select id="gender" name="gender" value={formData.gender} onChange={handleChange}>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div>
            <label htmlFor="mobileNumber">Mobile Number</label>
            <input
              type="tel"
              id="mobileNumber"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading 
            ? (isEditMode ? 'Updating...' : 'Registering...') 
            : (isEditMode ? 'Update' : 'Register')
          }
        </button>
        {/* Cancel button only for mobile */}
        <button type="button" className="mobile-cancel" onClick={handleCancel}>Cancel</button>
      </form>
      {error && <div className="error-popup">{error}</div>}
      {success && <div className="success-popup">{success}</div>}

      {/* Password Update Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            position: 'relative',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Close button */}
            <button
              className="modal-close-btn"
              onClick={closePasswordModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#666',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s, color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#007bff';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
              title="Close"
            >
              ×
            </button>

            <h3 style={{ marginBottom: '20px', marginTop: '10px' }}>Update Password</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '5px' }}>
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordModalData.currentPassword}
                onChange={handlePasswordModalChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '5px' }}>
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordModalData.newPassword}
                onChange={handlePasswordModalChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="confirmNewPassword" style={{ display: 'block', marginBottom: '5px' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={passwordModalData.confirmNewPassword}
                onChange={handlePasswordModalChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{
              color: '#dc3545',
              fontSize: '14px',
              marginBottom: '15px',
              padding: '8px',
              backgroundColor: passwordModalError ? '#f8d7da' : 'transparent',
              border: `1px solid ${passwordModalError ? '#f5c6cb' : 'transparent'}`,
              borderRadius: '4px',
              minHeight: '20px', // Reserve space for error message
              visibility: passwordModalError ? 'visible' : 'hidden'
            }}>
              {passwordModalError || 'Placeholder for error message'}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handlePasswordModalSubmit}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

